/**
 * 登入驗證模組
 */

//-----------------------------------------------------------------------
// 載入模組
import { AuthProvider } from 'ra-core';
import * as networkLib from "../lib/network";

//-----------------------------------------------------------------------
// defines
import { API_RESULT_CODE, MEMBER_GROUP, MEMBER_PERMISSION } from '../common/defines';
import { messages } from '../common/strings';
import { MEMBER_API, MemberLoginParams, MemberLoginRes, MemberMeRes } from '../common/protocol';

/** 管理員登入後要暫存的資料。 */
enum MEMBER_STORAGE_KEY
{
	/** 管理員資訊。 */
	Info					= 'memberInfo',
}

/** 管理員登入資訊。 */
export interface MemberAuthInfo
{
	/** 管理員 ID。 */
	id						: number

	/** 所屬群組。 */
	group					: MEMBER_GROUP

	/** 擁有權限。 */
	memberView				: boolean
	memberEdit				: boolean
	memberCreate			: boolean

	kolView					: boolean
	kolEdit					: boolean
	kolCreate				: boolean

	keywordView				: boolean
	keywordEdit				: boolean
	keywordCreate			: boolean

	manuallyChannelView		: boolean
	manuallyChannelEdit		: boolean
	manuallyChannelCreate	: boolean

	sourceView				: boolean
	sourceEdit				: boolean

	regionView				: boolean
	regionEdit				: boolean

	channelView				: boolean
	channelEdit				: boolean

	targetView				: boolean
	targetEdit				: boolean

	scheduleView			: boolean
	scheduleEdit			: boolean
	scheduleCreate			: boolean
}

/**
 * 檢查權限資訊列表中是否擁有指定權限。
 * @param permissions 	權限資訊列表。
 * @param permission	要檢查的權限。
 * @returns				是否有此權限。
 */
function hasPermission(permissions: MEMBER_PERMISSION[], permission: MEMBER_PERMISSION) : boolean
{
	console.log(JSON.stringify(permissions));

	for(let p of permissions)
	{
		if(p === permission)
			return true;
	}

	return false;
}

//-----------------------------------------------------------------------
// eslint-disable-next-line import/no-anonymous-default-export
export default () : AuthProvider =>
({
    login: async ({ username, password }) : Promise<void> =>
	{
		try
		{
			let	param : MemberLoginParams = { email: username, passcode: password };
			let	res = await networkLib.post(MEMBER_API.Path, MEMBER_API.Login, param) as MemberLoginRes;

			if(res.resultCode !== API_RESULT_CODE.Succeeded)
				throw Error(messages[res.resultCode]);

			let	authInfo : MemberAuthInfo =
			{
				id						: res.id,
				group					: res.group,
				memberView				: hasPermission(res.permissions, MEMBER_PERMISSION.MemberView),
				memberEdit				: hasPermission(res.permissions, MEMBER_PERMISSION.MemberEdit),
				memberCreate			: hasPermission(res.permissions, MEMBER_PERMISSION.MemberCreate),
				kolView					: hasPermission(res.permissions, MEMBER_PERMISSION.KolView),
				kolEdit					: hasPermission(res.permissions, MEMBER_PERMISSION.KolEdit),
				kolCreate				: hasPermission(res.permissions, MEMBER_PERMISSION.KolCreate),
				keywordView				: hasPermission(res.permissions, MEMBER_PERMISSION.KeywordView),
				keywordEdit				: hasPermission(res.permissions, MEMBER_PERMISSION.KeywordEdit),
				keywordCreate			: hasPermission(res.permissions, MEMBER_PERMISSION.KeywordCreate),
				manuallyChannelView		: hasPermission(res.permissions, MEMBER_PERMISSION.ManuallyChannelView),
				manuallyChannelEdit		: hasPermission(res.permissions, MEMBER_PERMISSION.ManuallyChannelEdit),
				manuallyChannelCreate	: hasPermission(res.permissions, MEMBER_PERMISSION.ManuallyChannelCreate),
				sourceView				: hasPermission(res.permissions, MEMBER_PERMISSION.SourceView),
				sourceEdit				: hasPermission(res.permissions, MEMBER_PERMISSION.SourceEdit),
				regionView				: hasPermission(res.permissions, MEMBER_PERMISSION.RegionView),
				regionEdit				: hasPermission(res.permissions, MEMBER_PERMISSION.RegionEdit),
				channelView				: hasPermission(res.permissions, MEMBER_PERMISSION.ChannelView),
				channelEdit				: hasPermission(res.permissions, MEMBER_PERMISSION.ChannelEdit),
				targetView				: hasPermission(res.permissions, MEMBER_PERMISSION.TargetView),
				targetEdit				: hasPermission(res.permissions, MEMBER_PERMISSION.TargetEdit),
				scheduleView			: hasPermission(res.permissions, MEMBER_PERMISSION.ScheduleView),
				scheduleEdit			: hasPermission(res.permissions, MEMBER_PERMISSION.ScheduleEdit),
				scheduleCreate			: hasPermission(res.permissions, MEMBER_PERMISSION.ScheduleCreate)
			};

			localStorage.setItem(MEMBER_STORAGE_KEY.Info, JSON.stringify(authInfo));
		}
		catch(e)
		{
			console.log("登入錯誤: " + e);
			throw e;
		}
	},

    logout: async (params: any) : Promise<void | false | string> =>
	{
		try
		{
			await networkLib.post(MEMBER_API.Path, MEMBER_API.Logout, {});

			localStorage.removeItem(MEMBER_STORAGE_KEY.Info);
		}
		catch(e)
		{
			console.log("登出錯誤: " + e);
			throw e;
		}
	},

    checkAuth: async (params: any) : Promise<void> =>
	{
		try
		{
			let	res = await networkLib.post(MEMBER_API.Path, MEMBER_API.Me, {}) as MemberMeRes;
			if(res.resultCode !== API_RESULT_CODE.Succeeded)
				return Promise.reject();
		}
		catch(e)
		{
			console.log("檢查登入狀態錯誤: " + e);
			throw e;
		}
	},

    checkError: async () : Promise<void> =>
	{
	},

    getPermissions: async (params: any) : Promise<any> =>
	{
		let	item = localStorage.getItem(MEMBER_STORAGE_KEY.Info);
		if(item === null)
		{
			let	allOff : MemberAuthInfo =
			{
				id						: -1,
				group					: MEMBER_GROUP.Guest,
				memberView				: false,
				memberEdit				: false,
				memberCreate			: false,
				kolView					: false,
				kolEdit					: false,
				kolCreate				: false,
				keywordView				: false,
				keywordEdit				: false,
				keywordCreate			: false,
				manuallyChannelView		: false,
				manuallyChannelEdit		: false,
				manuallyChannelCreate	: false,
				sourceView				: false,
				sourceEdit				: false,
				regionView				: false,
				regionEdit				: false,
				channelView				: false,
				channelEdit				: false,
				targetView				: false,
				targetEdit				: false,
				scheduleView			: false,
				scheduleEdit			: false,
				scheduleCreate			: false
			}

			return allOff;
		}

		return JSON.parse(item) as MemberAuthInfo;
	},
/*
    getIdentity: async () : Promise<UserIdentity> =>
	{
	}
*/
});
